/**
 * Ask Swirl API
 * Connects to AI chat service with context support
 * 
 * Expected payload:
 * {
 *   prompt: string,
 *   context: Array<{ id: string, type: string }>
 * }
 * 
 * Returns:
 * {
 *   content: string,
 *   referencedItems?: Array<{ id: string, type: string, label: string, thumbnail?: string }>
 * }
 */

import aiChatService from '../services/aiChatService';
import contentService from '../services/contentService';
import topicService from '../services/topicService';
import deepsearchService from '../services/deepsearchService';
import { supabase } from '../lib/supabase';

// Context cache per conversation
const contextCache = new Map();

// Throttle for context loading
let contextLoadTimer = null;

function throttleContextLoad(fn, delay = 350) {
  clearTimeout(contextLoadTimer);
  contextLoadTimer = setTimeout(fn, delay);
}

// Web search placeholder function
async function fetchWebResults(query) {
  console.log('[AskSwirl] Web search stub called with query:', query);
  // For now, return empty array - later swap to Bing or Serp API
  return [];
}

// Unified function to build item payload from deepsearch item
function buildItemPayload(item) {
              // Determine if this is an image based on source_type or content_type
              const isImage = item.source_type === 'image' || 
                            item.source_type === 'instagram' ||
                            item.meta?.filetype?.startsWith('image/') ||
                            item.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
              
              // Extract visual analysis data from backend processing
              const extractedText = item.extracted_text || item.meta?.extracted_text || '';
              const visionLabels = item.visual_labels || item.meta?.vision_labels || item.meta?.labels || [];
              const primaryTags = item.primary_tags || item.meta?.primary_tags || [];
              const descriptiveTags = item.descriptive_tags || item.meta?.descriptive_tags || [];
              const topicClusters = item.topic_clusters || item.meta?.topic_clusters || [];
              
              // Get vision description from meta or analysis_data
              const visionDescription = item.meta?.vision_description || 
                                       item.meta?.analysis_data?.vision_description ||
                                       item.meta?.description || '';
              
              // Build comprehensive visual description from all available data
              let visualDescription = '';
              if (visionDescription) {
                visualDescription = visionDescription;
              } else if (visionLabels.length > 0) {
                visualDescription = `Image contains: ${visionLabels.join(', ')}`;
                if (primaryTags.length > 0) {
                  visualDescription += `. Key elements: ${primaryTags.join(', ')}`;
                }
                if (topicClusters.length > 0) {
                  visualDescription += `. Topics: ${topicClusters.join(', ')}`;
                }
              } else if (primaryTags.length > 0) {
                visualDescription = `Image features: ${primaryTags.join(', ')}`;
              }
              
              // Combine extracted text and visual description for AI context
              let contentText = '';
              if (extractedText && visualDescription) {
                contentText = `Extracted text from image: ${extractedText}\n\nVisual content: ${visualDescription}`;
              } else if (extractedText) {
                contentText = `Extracted text from image: ${extractedText}`;
              } else if (visualDescription) {
                contentText = `Visual content: ${visualDescription}`;
              } else if (isImage) {
                // Fallback for images without processing yet - still tell AI it's an image
                contentText = `Image: ${item.title || item.description || 'Uploaded image'}. This is a visual image that the user is asking about. The image may still be processing for detailed analysis.`;
              } else {
                contentText = item.meta?.analysis_data?.summary || 
                            item.description || 
                            `Content: ${item.title || 'Uploaded file'}`;
              }
              
  return {
                id: item.id,
                title: item.title || item.description || 'Untitled',
                description: item.description || visualDescription || (isImage ? 'Image' : 'File'),
                url: item.url,
                transcription: contentText, // Combined OCR + visual description
                summary: item.meta?.analysis_data?.summary || visualDescription || item.description || item.title,
                thumbnail_url: item.thumbnail_url || item.meta?.thumbnail_url,
                content_type: item.source_type,
                visual_tags: visionLabels.length > 0 ? visionLabels : (primaryTags.length > 0 ? primaryTags : []),
                analysis_data: {
                  ...(item.meta?.analysis_data || {}),
                  vision_description: visualDescription,
                  vision_labels: visionLabels,
                  extracted_text: extractedText,
                  primary_tags: primaryTags,
                  descriptive_tags: descriptiveTags,
                  topic_clusters: topicClusters,
                },
  };
}

export async function askSwirl(payload, userId, conversationId = null) {
  const { prompt, context = [] } = payload;
  const startTime = performance.now();

  try {
    // CRITICAL: If no explicit context is provided, use backend RAG search
    // This automatically searches the user's Swirlspace content
    if (context.length === 0 && prompt && userId) {
      console.log('[AskSwirl] No context provided - using backend RAG to search Swirlspace...');
      
      const API_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4028';
      const backendUrl = API_URL.includes('/v1') ? API_URL : `${API_URL}/v1`;
      
      try {
        const response = await fetch(`${backendUrl}/ask`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            question: prompt,
            userId: userId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Backend ask failed: ${response.statusText}`);
        }

        const data = await response.json();
        
        console.log('[AskSwirl] ✅ Backend RAG response received:', {
          answerLength: data.answer?.length || 0,
          supportingItemsCount: data.supportingItems?.length || 0,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        });

        // Convert backend response format to frontend format
        const referencedItems = (data.supportingItems || []).map(item => ({
          id: item.id,
          type: 'item',
          label: item.title || 'Untitled',
          thumbnail: item.thumbnailUrl,
        }));

        return {
          data: {
            content: data.answer,
            referencedItems: referencedItems.length > 0 ? referencedItems : undefined,
          },
          error: null,
        };
      } catch (backendError) {
        console.error('[AskSwirl] Backend RAG failed, falling back to direct AI call:', backendError);
        // Fall through to original logic below
      }
    }
    
    // Check context cache first
    const cacheKey = conversationId ? `${userId}_${conversationId}` : null;
    if (cacheKey && contextCache.has(cacheKey)) {
      const cachedContext = contextCache.get(cacheKey);
      // Verify context items match (by comparing IDs)
      const contextIds = new Set(context.map(c => c.id).sort());
      const cachedIds = new Set(cachedContext.map(c => c.id).sort());
      const idsMatch = contextIds.size === cachedIds.size && 
                      [...contextIds].every(id => cachedIds.has(id));
      
      if (idsMatch) {
        console.log('[AskSwirl] Using cached context for this chat');
        // Use cached context, skip rebuilding
        const contextContent = cachedContext;
        
        // Continue with AI call using cached context
        const aiCallStart = performance.now();
        const response = await aiChatService.sendMessage(
          'current_chat',
          prompt || 'Analyze the provided context.',
          contextContent,
          []
        );
        const aiCallTime = performance.now() - aiCallStart;
        
        console.log('[AskSwirl] ⏱️ AI response received (cached context):', {
          hasContent: !!response.data?.content,
          contentLength: response.data?.content?.length || 0,
          aiCallTime: `${aiCallTime.toFixed(2)}ms`,
          totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
        });

        if (response.error) {
          throw new Error(response.error);
        }

        const referencedItems = contextContent.slice(0, 6).map(item => ({
          id: item.id,
          type: 'item',
          label: item.title || 'Untitled',
          thumbnail: item.thumbnail_url,
        }));

        return {
          data: {
            content: response.data.content,
            referencedItems: referencedItems.length > 0 ? referencedItems : undefined,
          },
          error: null,
        };
      } else {
        // Context changed, clear cache for this conversation
        contextCache.delete(cacheKey);
      }
    }

    // Load context content from chip IDs
    let contextContent = [];
    
    if (context.length > 0 && userId) {
      const contextLoadStart = performance.now();
      let deepsearchItemsCache = null;
      let itemsLoaded = false;
      
      // Check if we need to load items (for 'item' or 'tag' types)
      const needsItemsLoad = context.some(ctx => ctx.type === 'item' || ctx.type === 'tag');
      
      if (needsItemsLoad) {
        // Get unique item IDs from context
        const contextItemIds = context
          .filter(ctx => ctx.type === 'item')
          .map(ctx => ctx.id)
          .filter(Boolean);
        
        if (contextItemIds.length > 0) {
          console.log(`[AskSwirl] ⚡ Loading ${contextItemIds.length} specific context items by ID...`);
          // Query specific items by ID from deepsearch.items - NO FALLBACK
          const { data: specificItems, error: specificError } = await supabase
            .schema('deepsearch')
            .from('items')
            .select('id, title, description, url, source_type, auto_tags, extracted_text, visual_labels, primary_tags, descriptive_tags, topic_clusters, color_palette, primary_color, color_mood, created_at, meta')
            .eq('user_id', userId)
            .in('id', contextItemIds);
          
          if (!specificError && specificItems) {
            deepsearchItemsCache = specificItems;
            itemsLoaded = true;
            console.log(`[AskSwirl] ✅ Loaded ${deepsearchItemsCache.length} specific items in ${(performance.now() - contextLoadStart).toFixed(2)}ms`);
          } else {
            console.warn(`[AskSwirl] ⚠️ Failed to load specific items:`, specificError);
            // Don't fallback to loading all items - just use empty cache
            deepsearchItemsCache = [];
            itemsLoaded = true;
          }
        } else {
          // No specific IDs needed for items, but tags might need items
          // Only load if tags are present
          const hasTags = context.some(ctx => ctx.type === 'tag');
          if (hasTags) {
            // For tags, we need all items to filter - but this should be rare
            console.log('[AskSwirl] ⚡ Loading items for tag filtering...');
            const itemsResult = await deepsearchService.getItems(userId);
            deepsearchItemsCache = itemsResult.data || [];
            itemsLoaded = true;
            console.log(`[AskSwirl] ✅ Loaded ${deepsearchItemsCache.length} items for tags in ${(performance.now() - contextLoadStart).toFixed(2)}ms`);
          }
        }
      }
      
      // Process context items
      for (const ctx of context) {
        try {
          if (ctx.type === 'item') {
            const item = deepsearchItemsCache?.find(i => i.id === ctx.id);
            
            if (item) {
              // Use unified buildItemPayload function
              const itemPayload = buildItemPayload(item);
              contextContent.push(itemPayload);
              
              console.log(`[AskSwirl] Loaded context item: ${item.id}`, {
                title: item.title,
                hasExtractedText: !!itemPayload.analysis_data.extracted_text,
                hasVisionLabels: itemPayload.analysis_data.vision_labels?.length > 0,
                contentTextLength: itemPayload.transcription?.length || 0,
              });
            } else {
              // Item not found in cache - query directly by ID (no fallback to getItems)
              console.log(`[AskSwirl] Item ${ctx.id} not in cache, querying directly...`);
              const { data: directItem, error: directError } = await supabase
                .schema('deepsearch')
                .from('items')
                .select('id, title, description, url, source_type, auto_tags, extracted_text, visual_labels, primary_tags, descriptive_tags, topic_clusters, color_palette, primary_color, color_mood, created_at, meta')
                .eq('id', ctx.id)
                .eq('user_id', userId)
                .single();
              
              if (!directError && directItem) {
                const itemPayload = buildItemPayload(directItem);
                contextContent.push(itemPayload);
                console.log(`[AskSwirl] Loaded deepsearch item directly: ${directItem.id}`);
              } else {
                // Not in deepsearch.items, try instagram_content as last resort
                console.log(`[AskSwirl] Item ${ctx.id} not in deepsearch.items, trying instagram_content...`);
                const { data } = await contentService.getContentById(ctx.id);
                if (data) {
                  // Determine if this is an image
                  const isImage = data.content_type === 'image' || 
                                data.content_type === 'post' ||
                                data.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                  
                  // For images, use visual tags or analysis data
                  const visualDescription = data.analysis_data?.visual_tags?.join(', ') ||
                                           data.analysis_data?.visual_description ||
                                           data.ai_analysis?.summary;
                  
                  // Use transcription, visual description, or summary
                  let contentText = data.transcription;
                  if (!contentText && isImage) {
                    contentText = visualDescription || 
                                `Image: ${data.title || data.description || 'Uploaded image'}. ${data.description || 'Visual content that the user is asking about.'}`;
                  } else if (!contentText) {
                    contentText = data.ai_analysis?.summary || 
                                data.description || 
                                `Content: ${data.title || 'Uploaded file'}`;
                  }
                  
                  contextContent.push({
                    id: data.id,
                    title: data.title,
                    description: data.description || visualDescription || (isImage ? 'Image' : 'File'),
                    url: data.url,
                    transcription: contentText,
                    summary: data.ai_analysis?.summary || visualDescription || data.description || data.title,
                    thumbnail_url: data.thumbnail_url,
                    content_type: data.content_type,
                    visual_tags: data.analysis_data?.visual_tags || [],
                    analysis_data: data.analysis_data || data.ai_analysis || {},
                  });
                  
                  console.log(`[AskSwirl] Loaded instagram_content item: ${data.id}`);
                }
              }
            }
          } else if (ctx.type === 'collection') {
            // Get all items in collection
            const { data: collectionItems } = await contentService.getContentByCollection(userId, ctx.id);
            if (collectionItems) {
              contextContent.push(...collectionItems.map(item => ({
                id: item.id,
                title: item.title,
                description: item.description,
                url: item.url,
                transcription: item.transcription,
                summary: item.ai_analysis?.summary,
                thumbnail_url: item.thumbnail_url,
                content_type: item.content_type,
              })));
            }
          } else if (ctx.type === 'focus') {
            // Focus boards are collections/topics
            const { data: focusItems } = await contentService.getContentByCollection(userId, ctx.id);
            if (focusItems) {
              contextContent.push(...focusItems.map(item => ({
                id: item.id,
                title: item.title,
                description: item.description,
                url: item.url,
                transcription: item.transcription,
                summary: item.ai_analysis?.summary,
                thumbnail_url: item.thumbnail_url,
                content_type: item.content_type,
              })));
            }
          } else if (ctx.type === 'tag') {
            // For tags, we need all items to filter - load only if not already loaded
            if (!itemsLoaded || !deepsearchItemsCache) {
              const tagLoadStart = performance.now();
              const itemsResult = await deepsearchService.getItems(userId);
              deepsearchItemsCache = itemsResult.data || [];
              itemsLoaded = true;
              console.log(`[AskSwirl] ⚡ Loaded items for tag filtering in ${(performance.now() - tagLoadStart).toFixed(2)}ms`);
            }
            
            const taggedItems = (deepsearchItemsCache || []).filter(item => 
              item.auto_tags?.includes(ctx.id) || item.primary_tags?.includes(ctx.id)
            );
            // Use unified buildItemPayload for tagged items
            contextContent.push(...taggedItems.map(item => buildItemPayload(item)));
          }
        } catch (error) {
          console.error(`Error loading context item ${ctx.id}:`, error);
        }
      }
    }

    // Cache context for this conversation
    if (cacheKey && contextContent.length > 0) {
      contextCache.set(cacheKey, contextContent);
      console.log(`[AskSwirl] Cached context for conversation ${conversationId}`);
    }

    // Log context content for debugging
    const contextBuildTime = performance.now() - startTime;
    console.log('[AskSwirl] ⏱️ Context building complete:', {
      prompt: prompt || '',
      contextItemsCount: contextContent.length,
      contextItems: contextContent.map(c => ({ id: c.id, title: c.title })),
      buildTime: `${contextBuildTime.toFixed(2)}ms`,
      cached: cacheKey && contextCache.has(cacheKey)
    });
    
    // Web search integration hook
    const useWebSearch = true; // temporary, can later be driven by UI or settings
    let webResults = [];
    
    if (useWebSearch && prompt) {
      // Simple rule: needs fresh data if contains "news", "today", or question mark and no Swirl items
      const needsFreshData = (
        prompt.toLowerCase().includes('news') ||
        prompt.toLowerCase().includes('today') ||
        (prompt.includes('?') && contextContent.length === 0)
      );
      
      if (needsFreshData) {
        webResults = await fetchWebResults(prompt);
      }
    }

    // Use existing AI chat service with web results
    const aiCallStart = performance.now();
    const response = await aiChatService.sendMessage(
      'current_chat',
      prompt || 'Analyze the provided context.',
      contextContent,
      [], // Conversation history - could be passed if needed
      webResults // Pass web results
    );
    const aiCallTime = performance.now() - aiCallStart;
    
    console.log('[AskSwirl] ⏱️ AI response received:', {
      hasContent: !!response.data?.content,
      contentLength: response.data?.content?.length || 0,
      citationsCount: response.data?.citations?.length || 0,
      hasSummary: !!response.data?.summary,
      followUpsCount: response.data?.followUps?.length || 0,
      aiCallTime: `${aiCallTime.toFixed(2)}ms`,
      totalTime: `${(performance.now() - startTime).toFixed(2)}ms`
    });

    if (response.error) {
      throw new Error(response.error);
    }

    // Log Answer Engine completion
    console.log('[AnswerEngine] Answer, citations, summary, followUps ready');

    return {
      data: {
        content: response.data.content,
        referencedItems: response.data.citations || [],
        summary: response.data.summary || null,
        followUps: response.data.followUps || [],
      },
      error: null,
    };
  } catch (error) {
    console.error('Ask Swirl API Error:', error);
    return {
      data: null,
      error: error.message,
    };
  }
}

